import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Position, PositionResponse } from '../../../models/Position';
import { PositionService } from '../../../services/position.service';
import { DepartmentService } from '../../../services/department.service';
import { Department } from '../../../models/Department';
import { ErrorService } from '../../../shared/services/error.service';

@Component({
  selector: 'app-positions',
  templateUrl: './positions.component.html',
  styleUrls: ['./positions.component.css']
})
export class PositionsComponent implements OnInit {
  departmentId: number = 0;
  department: Department | null = null;
  positions: Position[] = [];
  loading = false;
  error: string | null = null;

  actions = [
    {
      label: 'Editar',
      icon: 'fas fa-edit',
      action: 'edit',
      class: 'btn-warning'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash',
      action: 'delete',
      class: 'btn-danger'
    },
    {
      label: 'Ativar/Desativar',
      icon: 'fas fa-toggle-on',
      action: 'toggleActive',
      class: 'btn-secondary'
    }
  ];

  columns = [
    { titulo: 'Nome', label: 'name' },
    { titulo: 'Nível', label: 'level', formatter: (value: any) => this.formatLevel(value) },
    { titulo: 'Descrição', label: 'description' },
    { titulo: 'Carga Horária', label: 'defaultWorkloadHours', formatter: (value: any) => `${value}h` },
    { titulo: 'Status', label: 'isActive', formatter: (value: any) => this.formatActiveStatus(value) },
    { titulo: 'Data Criação', label: 'createdAt', formatter: (value: any) => this.formatDate(value) }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private positionService: PositionService,
    private departmentService: DepartmentService,
    private errorService: ErrorService
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.departmentId = +params['id'];
      if (this.departmentId) {
        this.loadDepartment();
        this.loadPositions();
      }
    });
  }

  loadDepartment(): void {
    // Buscar informações do departamento para exibir o nome e gerente
    this.departmentService.getDepartmentsPaginated(1, 1000).subscribe({
      next: (response) => {
        this.department = response.departments.find(d => d.id === this.departmentId) || null;
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
      }
    });
  }

  loadPositions(): void {
    this.loading = true;
    this.error = null;

    this.positionService.getPositionsByDepartment(this.departmentId).subscribe({
      next: (response: PositionResponse) => {
        this.positions = response.positions || [];
        this.loading = false;
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.error = 'Erro ao carregar cargos';
        this.loading = false;
      }
    });
  }

  onActionClick(actionData: { action: string; item: Position }): void {
    const { action, item } = actionData;

    switch (action) {
      case 'edit':
        this.editPosition(item);
        break;
      case 'delete':
        this.deletePosition(item);
        break;
      case 'toggleActive':
        this.toggleActivePosition(item);
        break;
      default:
        break;
    }
  }

  editPosition(position: Position): void {
    if (!position || !position.id) {
      this.error = 'Cargo inválido para edição';
      return;
    }
    this.router.navigate(['/hr/departments', this.departmentId, 'positions', position.id, 'edit']);
  }

  deletePosition(position: Position): void {
    if (!position || !position.id) {
      this.error = 'Cargo inválido para exclusão';
      return;
    }

    if (!confirm(`Tem certeza que deseja excluir o cargo "${position.name}"?`)) {
      return;
    }

    this.loading = true;
    this.error = null;

    this.positionService.deletePosition(position.id).subscribe({
      next: () => {
        this.loadPositions();
      },
      error: (err) => {
        this.error = 'Erro ao excluir cargo';
        this.loading = false;
        this.errorService.handleHttpError(err);
      }
    });
  }

  activatePosition(position: Position): void {
    if (!position || !position.id) {
      this.error = 'Cargo inválido para ativação';
      return;
    }

    this.positionService.activatePosition(position.id).subscribe({
      next: () => {
        this.loadPositions();
      },
      error: (err) => {
        this.error = 'Erro ao ativar cargo';
        this.errorService.handleHttpError(err);
      }
    });
  }

  deactivatePosition(position: Position): void {
    if (!position || !position.id) {
      this.error = 'Cargo inválido para desativação';
      return;
    }

    this.positionService.deactivatePosition(position.id).subscribe({
      next: () => {
        this.loadPositions();
      },
      error: (err) => {
        this.error = 'Erro ao desativar cargo';
        this.errorService.handleHttpError(err);
      }
    });
  }

  private toggleActivePosition(position: Position): void {
    if (!position) {
      this.error = 'Cargo inválido';
      return;
    }

    if (position.isActive) {
      this.deactivatePosition(position);
    } else {
      this.activatePosition(position);
    }
  }

  formatLevel(value: any): string {
    return value || 'N/A';
  }

  formatActiveStatus(value: any): string {
    return value ? 'Ativo' : 'Inativo';
  }

  formatDate(value: any): string {
    if (!value) return 'N/A';
    const date = new Date(value);
    return date.toLocaleDateString('pt-BR');
  }

  newPosition(): void {
    this.router.navigate(['/hr/departments', this.departmentId, 'positions', 'new']);
  }

  goBack(): void {
    this.router.navigate(['/hr/departments']);
  }
}

